import { useCallback, useEffect, useState } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import useElementVisible from "../hooks/useElementVisible";
import useSortableData from "../hooks/useSortableData";
import DataTable from "../components/UI/DataTable";
import { DataTableColumn } from "../models/DataTable";
import { sortableDataExportsEnabled } from "../utils/features";
import { DataExport } from "../models/serviceModels/DataExport";
import { getDataExports } from "../services/organizationService";
import "./ManageDataExports.css";

const TAKE = 50;
const DOWNLOADABLE_STATUSES = ["Completed", "CompletedWithErrors"];

const formatStatus = (status?: string | null) => {
    if (!status) {
        return "";
    }

    return status
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ");
};

const formatDateTime = (value?: string | null, fallback = "") => {
    if (!value) {
        return fallback;
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "numeric",
            minute: "2-digit"
        });
    }

    return value.slice(0, 16).replace("T", " ");
};

const formatDateOnly = (value: string) => {
    const [datePart] = value.split("T");
    if (!datePart) {
        return value;
    }

    const [year, month, day] = datePart.split("-");
    return month && day && year ? `${month}/${day}/${year}` : datePart;
};

const formatDateRange = (dataExport: DataExport) => {
    if (!dataExport.dateRangeStart && !dataExport.dateRangeEnd) {
        return "All created or updated dates";
    }

    const start = dataExport.dateRangeStart ? formatDateOnly(dataExport.dateRangeStart) : "Beginning";
    const end = dataExport.dateRangeEnd ? formatDateOnly(dataExport.dateRangeEnd) : "Now";
    return `${start} to ${end}`;
};

const getCreatedBy = (dataExport: DataExport) => {
    return [dataExport.createdByFirstName, dataExport.createdByLastName].filter(Boolean).join(" ") || dataExport.createdBy;
};

const canHaveDownload = (dataExport: DataExport) => DOWNLOADABLE_STATUSES.includes(dataExport.status) && !!dataExport.s3Key;

const ManageDataExportHistory = () => {
    const [exports, setExports] = useState<DataExport[]>([]);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(false);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [apiError, setApiError] = useState<any>();
    const [pagingReference, pagingVisible] = useElementVisible<HTMLDivElement>();
    const [serverSort, setServerSort] = useState<{ sortBy?: string; sortDirection?: 'asc' | 'desc' }>({});
    const getSortValue = useCallback((dataExport: DataExport, key: string) => {
        if (key === 'ranBy') return getCreatedBy(dataExport);
        if (key === 'files') return dataExport.selectedDatasets.length;
        return dataExport[key as keyof DataExport];
    }, []);
    const { data: sortedExports, sortBy, sortDirection, onSort } = useSortableData(exports, getSortValue);

    useEffect(() => {
        document.title = "Export History";
        loadExports(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (skip === 0) {
            return;
        }

        loadExports(skip, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skip]);

    useEffect(() => {
        if (pagingVisible && hasMore && !loading && !paging) {
            setSkip(current => current + TAKE);
        }
    }, [pagingVisible, hasMore, loading, paging]);

    const loadExports = async (nextSkip: number, append: boolean, sort = serverSort) => {
        if (append) {
            setPaging(true);
        } else {
            setLoading(true);
        }
        setApiError(undefined);

        try {
            const result = await getDataExports(nextSkip, TAKE, sort.sortBy, sort.sortDirection);
            setExports(current => append ? [...current, ...result] : result);
            setHasMore(result.length === TAKE);
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setLoading(false);
            setPaging(false);
        }
    };

    const handleSort = (key: string) => {
        onSort(key);
        if (!sortableDataExportsEnabled) return;

        const sort = {
            sortBy: key,
            sortDirection: sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc'
        } as const;
        setServerSort(sort);
        setSkip(0);
        loadExports(0, false, sort);
    };

    return (
        <section id="main-content" tabIndex={-1}>
            <div className="data-export-history-header">
                <Button id="back-to-data-exports"
                    text="Back"
                    className="small secondary"
                    href="/admin/data-exports"
                    icon={<i className="ri-arrow-left-s-line" />}
                    iconLocation="start" />
                <h1>Export History</h1>
            </div>
            <div className="data-export-shell">
                <ApiErrorMessages error={apiError} />
                <div className="data-export-history-note">
                    Completed export downloads are available for one month. After that, the history remains visible but the ZIP download expires.
                </div>
                {loading && <Loader />}
                {!loading && exports.length === 0 && <div className="data-export-empty">No exports found</div>}
                {exports.length > 0 && <div className="data-export-table-scroll">
                    <DataTable
                        columns={[
                            { key: 'created', label: 'Created', sortable: true },
                            { key: 'ranBy', label: 'Ran By', sortable: true },
                            { key: 'dateRange', label: 'Date Range', sortable: false },
                            { key: 'status', label: 'Status', sortable: true },
                            { key: 'files', label: 'Files', sortable: false },
                            { key: 'processedCount', label: 'Processed', sortable: true },
                            { key: 'successCount', label: 'Completed', sortable: true },
                            { key: 'failedCount', label: 'Failed', sortable: true },
                            { key: 'cancelledCount', label: 'Cancelled', sortable: true },
                            { key: 'completed', label: 'Finished', sortable: true },
                            { key: 'actions', label: '', sortable: false }
                        ] as DataTableColumn[]}
                        data={sortedExports}
                        sortBy={sortBy}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        sortingEnabled={sortableDataExportsEnabled}
                        renderRow={dataExport => <tr key={dataExport.id} className="table-row">
                                <td>{formatDateTime(dataExport.created)}</td>
                                <td>{getCreatedBy(dataExport)}</td>
                                <td>{formatDateRange(dataExport)}</td>
                                <td>{formatStatus(dataExport.status)}</td>
                                <td>{dataExport.selectedDatasets.length}</td>
                                <td>{dataExport.processedCount}</td>
                                <td>{dataExport.successCount}</td>
                                <td>{dataExport.failedCount}</td>
                                <td>{dataExport.cancelledCount || 0}</td>
                                <td>{formatDateTime(dataExport.completed, "Not finished")}</td>
                                <td>
                                    {dataExport.downloadUrl && <Button id={`download-data-export-${dataExport.id}`}
                                        text="Download"
                                        className="action small"
                                        href={dataExport.downloadUrl}
                                        newWindow={true}
                                        icon={<i className="ri-download-line"></i>}
                                        iconLocation="start" />}
                                    {!dataExport.downloadUrl && canHaveDownload(dataExport) && <span className="data-export-muted">Expired</span>}
                                </td>
                            </tr>} />
                </div>}
                {paging && <Loader />}
                {hasMore && <div ref={pagingReference} className="data-export-scroll-sentinel"></div>}
            </div>
        </section>
    );
};

export default ManageDataExportHistory;
